import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { persist } from "zustand/middleware"; // Add this import
import { CheckoutStep } from "../types/common";
import { KycModel, KycFormInput } from "../types/kyc";
import { useCartStore } from "./useCartStore";

interface ContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
}

interface CheckoutFlowState {
  // Order tracking
  orderId: string | null;
  setOrderId: (id: string | null) => void;
  clearOrderId: () => void;

  // Contact information
  contactInfo: ContactInfo;

  // Verification states
  isEmailVerified: boolean;
  isKycVerified: boolean;

  // Flow control
  currentStep: CheckoutStep;
  kycInfo: KycModel | null;

  // KYC form validation
  kycFormData: KycFormInput | null;
  kycFormErrors: Record<string, string>;
  isKycFormValid: boolean;

  // User sync tracking
  lastSyncedUserId: string | null;

  // Contact info setters
  setContactInfo: (contactInfo: Partial<ContactInfo>) => void;
  setEmail: (email: string) => void;
  setFirstName: (firstName: string) => void;
  setLastName: (lastName: string) => void;
  setPhoneNumber: (phoneNumber: string) => void;
  setCountryCode: (countryCode: string) => void;

  // User data sync
  syncUserData: (user: any) => void;
  clearUserData: () => void;

  // KYC form management
  setKycFormData: (data: KycFormInput) => void;
  setKycFormValidation: (
    isValid: boolean,
    errors: Record<string, string>
  ) => void;
  clearKycForm: () => void;

  // Verification methods
  verifyEmail: (result: {
    isEmailVerified: boolean;
    isKycVerified: boolean;
  }) => void;
  setKycVerified: (status: boolean) => void;
  setKycInfo: (data: KycModel) => void;

  // Navigation methods
  nextStep: () => CheckoutStep;
  prevStep: () => CheckoutStep;
  goToStep: (step: CheckoutStep) => void;

  // Validation methods
  canProceedToNextStep: () => { canProceed: boolean; error?: string };

  // Utility methods
  clearContactInfo: () => void;
  isContactInfoComplete: () => boolean;
  resetStore: () => void;

  // Enhanced validation methods
  validateCurrentStep: () => {
    isValid: boolean;
    redirectTo?: CheckoutStep;
    reason?: string;
  };
  getValidStepForUser: () => CheckoutStep;
  canAccessStep: (step: CheckoutStep) => {
    canAccess: boolean;
    reason?: string;
  };
}

const nextStepMap: Record<CheckoutStep, CheckoutStep> = {
  select: "contact",
  contact: "kyc",
  kyc: "payment",
  payment: "payment",
};

const prevStepMap: Record<CheckoutStep, CheckoutStep> = {
  select: "select",
  contact: "select",
  kyc: "contact",
  payment: "kyc",
};

const initialContactInfo: ContactInfo = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  countryCode: "+856", // Default to Laos
};

// const emptyKycForm: KycFormInput = {
//   name: "",
//   surname: "",
//   birth_day: "",
//   gender: "",
//   country: "",
//   passport_no: "",
//   issue_date: "",
//   expiry_date: "",
//   nation: "",
//   doc_photo: null,
//   personal_photo: null,
// };

// Helper function to parse name into first and last name
const parseName = (fullName: string) => {
  const parts = fullName.trim().split(" ");
  const firstName = parts[0] || "";
  const lastName = parts.slice(1).join(" ") || "";
  return { firstName, lastName };
};

export const useCheckoutFlowStore = create<CheckoutFlowState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // Order tracking
        orderId: null,

        setOrderId: (id) => set({ orderId: id }),

        clearOrderId: () => set({ orderId: null }),
        // Initial state
        contactInfo: initialContactInfo,
        isEmailVerified: false,
        isKycVerified: false,
        currentStep: "select" as CheckoutStep,
        kycInfo: null,
        kycFormData: null,
        kycFormErrors: {},
        isKycFormValid: false,
        lastSyncedUserId: null,

        // Contact info setters
        setContactInfo: (newContactInfo) =>
          set((state) => ({
            contactInfo: { ...state.contactInfo, ...newContactInfo },
          })),

        setEmail: (email) =>
          set((state) => ({
            contactInfo: { ...state.contactInfo, email },
          })),

        setFirstName: (firstName) =>
          set((state) => ({
            contactInfo: { ...state.contactInfo, firstName },
          })),

        setLastName: (lastName) =>
          set((state) => ({
            contactInfo: { ...state.contactInfo, lastName },
          })),

        setPhoneNumber: (phoneNumber) =>
          set((state) => ({
            contactInfo: { ...state.contactInfo, phoneNumber },
          })),

        setCountryCode: (countryCode) =>
          set((state) => ({
            contactInfo: { ...state.contactInfo, countryCode },
          })),

        // User data sync methods
        syncUserData: (user) => {
          const { lastSyncedUserId, contactInfo } = get();
          // console.log(`contact info ${contactInfo.phoneNumber}`);

          // Only sync if it's a different user or contact info is empty
          if (
            (user && user.cusId !== lastSyncedUserId) ||
            (!contactInfo.email && !contactInfo.firstName)
          ) {
            const { firstName, lastName } = parseName(user.name || "");

            set({
              contactInfo: {
                firstName: firstName,
                lastName: lastName,
                email: user.email || "",
                phoneNumber: contactInfo.phoneNumber || "", // Keep existing phone if any
                countryCode: contactInfo.countryCode || "+856", // Keep existing country code
              },
              lastSyncedUserId: user.cusId,
              // Auto-verify email if user is authenticated via OAuth
              isEmailVerified: true,
            });
          }
        },

        clearUserData: () => {
          set({
            contactInfo: initialContactInfo,
            lastSyncedUserId: null,
            isEmailVerified: false,
            isKycVerified: false,
            kycInfo: null,
            kycFormData: null,
            kycFormErrors: {},
            isKycFormValid: false,
          });
        },

        // KYC form management
        setKycFormData: (data) => set({ kycFormData: data }),

        setKycFormValidation: (isValid, errors) =>
          set({ isKycFormValid: isValid, kycFormErrors: errors }),

        clearKycForm: () =>
          set({
            kycFormData: null,
            kycFormErrors: {},
            isKycFormValid: false,
          }),

        // Verification methods
        verifyEmail: ({ isEmailVerified, isKycVerified }) =>
          set({
            isEmailVerified,
            isKycVerified,
            currentStep: isKycVerified ? "payment" : "kyc",
          }),

        setKycVerified: (status) =>
          set((state) => ({
            isKycVerified: status,
            currentStep: status ? "payment" : state.currentStep,
          })),

        setKycInfo: (data) => set({ kycInfo: data }),

        validateCurrentStep: () => {
          const { currentStep, isEmailVerified, isKycVerified, contactInfo } =
            get();

          switch (currentStep) {
            case "select":
              // Always valid - this is the starting point
              return { isValid: true };

            case "contact":
              // Contact step is always accessible after select
              return { isValid: true };

            case "kyc":
              // Can only access KYC if contact info is complete and email is verified
              if (!isEmailVerified) {
                return {
                  isValid: false,
                  redirectTo: "contact" as CheckoutStep,
                  reason: "Email verification required before KYC",
                };
              }

              const isContactComplete = !!(
                contactInfo.firstName.trim() &&
                contactInfo.lastName.trim() &&
                contactInfo.email.trim() &&
                contactInfo.phoneNumber.trim() &&
                contactInfo.countryCode.trim()
              );

              if (!isContactComplete) {
                return {
                  isValid: false,
                  redirectTo: "contact" as CheckoutStep,
                  reason: "Complete contact information required before KYC",
                };
              }

              return { isValid: true };

            case "payment":
              // Payment requires: email verified, KYC verified
              if (!isEmailVerified) {
                return {
                  isValid: false,
                  redirectTo: "contact" as CheckoutStep,
                  reason: "Email verification required",
                };
              }

              if (!isKycVerified) {
                return {
                  isValid: false,
                  redirectTo: "kyc" as CheckoutStep,
                  reason: "KYC verification required before payment",
                };
              }

              return { isValid: true };

            default:
              return {
                isValid: false,
                redirectTo: "select" as CheckoutStep,
                reason: "Invalid step",
              };
          }
        },

        getValidStepForUser: () => {
          const { isEmailVerified, isKycVerified, contactInfo, currentStep } =
            get();
          // If user is currently on select step, allow them to stay there
          if (currentStep === "select") {
            return "select";
          }

          // Check if contact info is complete
          const isContactComplete = !!(
            contactInfo.firstName.trim() &&
            contactInfo.lastName.trim() &&
            contactInfo.email.trim() &&
            contactInfo.phoneNumber.trim() &&
            contactInfo.countryCode.trim()
          );

          // Determine the furthest valid step
          if (isEmailVerified && isKycVerified) {
            return "payment";
          } else if (isEmailVerified && isContactComplete) {
            return "kyc";
          } else {
            return "contact";
          }
        },

        canAccessStep: (step: CheckoutStep) => {
          const { isEmailVerified, isKycVerified, contactInfo } = get();

          const isContactComplete = !!(
            contactInfo.firstName.trim() &&
            contactInfo.lastName.trim() &&
            contactInfo.email.trim() &&
            contactInfo.phoneNumber.trim() &&
            contactInfo.countryCode.trim()
          );

          switch (step) {
            case "select":
              return { canAccess: true };

            case "contact":
              return { canAccess: true };

            case "kyc":
              if (!isEmailVerified || !isContactComplete) {
                return {
                  canAccess: false,
                  reason:
                    "Complete contact information and email verification required",
                };
              }
              return { canAccess: true };

            case "payment":
              if (!isEmailVerified) {
                return {
                  canAccess: false,
                  reason: "Email verification required",
                };
              }
              if (!isKycVerified) {
                return {
                  canAccess: false,
                  reason: "KYC verification required",
                };
              }
              return { canAccess: true };

            default:
              return { canAccess: false, reason: "Invalid step" };
          }
        },

        // Validation methods
        canProceedToNextStep: () => {
          const {
            currentStep,
            isEmailVerified,
            isKycVerified,
            isKycFormValid,
            contactInfo,
          } = get();
          const localCart = useCartStore.getState().cartItems;

          switch (currentStep) {
            case "select":
              if (localCart.length === 0) {
                return {
                  canProceed: false,
                  error: "Please add at least one item to the cart.",
                };
              }
              return { canProceed: true };

            case "contact":
              if (!isEmailVerified) {
                return {
                  canProceed: false,
                  error: "Please verify your email address before proceeding.",
                };
              }
              const isContactComplete = !!(
                contactInfo.firstName.trim() &&
                contactInfo.lastName.trim() &&
                contactInfo.email.trim() &&
                contactInfo.phoneNumber.trim() &&
                contactInfo.countryCode.trim()
              );
              if (!isContactComplete) {
                return {
                  canProceed: false,
                  error: "Please complete all contact information fields.",
                };
              }
              return { canProceed: true };

            case "kyc":
              if (!isKycVerified) {
                if (!isKycFormValid) {
                  return {
                    canProceed: false,
                    error:
                      "Please complete all required KYC verification fields correctly.",
                  };
                }
              }
              return { canProceed: true };

            case "payment":
              return { canProceed: true };

            default:
              return { canProceed: false, error: "Invalid step." };
          }
        },

        // Navigation methods
        nextStep: () => {
          const { currentStep, isKycVerified } = get();
          let next = nextStepMap[currentStep];
          // Skip KYC step entirely if the user is already verified
          if (currentStep === "contact" && isKycVerified) {
            next = "payment";
          }
          set({ currentStep: next });
          return next;
        },

        prevStep: () => {
          const { currentStep, isKycVerified } = get();
          let prev = prevStepMap[currentStep];
          // Mirror the skip when navigating backwards from payment
          if (currentStep === "payment" && isKycVerified) {
            prev = "contact";
          }
          set({ currentStep: prev });
          return prev;
        },

        goToStep: (step: CheckoutStep) => {
          const { canAccess, reason } = get().canAccessStep(step);

          if (!canAccess) {
            console.warn(`Cannot access step ${step}: ${reason}`);
            const validStep = get().getValidStepForUser();
            set({ currentStep: validStep });
            return;
          }

          set({ currentStep: step });
        },

        // Utility methods
        clearContactInfo: () => set({ contactInfo: initialContactInfo }),

        isContactInfoComplete: () => {
          const { contactInfo } = get();
          return !!(
            contactInfo.firstName.trim() &&
            contactInfo.lastName.trim() &&
            contactInfo.email.trim() &&
            contactInfo.phoneNumber.trim() &&
            contactInfo.countryCode.trim()
          );
        },

        // Reset entire store to initial state
        resetStore: () => {
          set({
            contactInfo: initialContactInfo,
            isEmailVerified: false,
            isKycVerified: false,
            currentStep: "select" as CheckoutStep,
            kycInfo: null,
            kycFormData: null,
            kycFormErrors: {},
            isKycFormValid: false,
            lastSyncedUserId: null,
          });
        },
      }),
      {
        name: "checkout-flow-storage", // localStorage key
        // Only persist specific fields
        partialize: (state) => ({
          contactInfo: state.contactInfo,
          isEmailVerified: state.isEmailVerified,
          isKycVerified: state.isKycVerified,
          kycInfo: state.kycInfo,
          lastSyncedUserId: state.lastSyncedUserId,
          orderId: state.orderId,
        }),
        version: 1,
      }
    )
  )
);
